import { type DependencyList, useCallback, useEffect, useState } from 'react';

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  reload: () => void;
}

export function useAsync<T>(fn: () => Promise<T>, deps: DependencyList): AsyncState<T> {
  const [state, setState] = useState<{ data: T | null; loading: boolean; error: Error | null }>({
    data: null,
    loading: true,
    error: null,
  });
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: 재구독은 deps + reload(nonce)로만 통제한다. fn은 매 렌더 새 참조라 의존 배열에서 의도적으로 제외한다.
  useEffect(() => {
    let active = true;
    setState((prev) => (prev.loading && prev.error === null ? prev : { ...prev, loading: true, error: null }));
    fn().then(
      (data) => {
        if (active) setState({ data, loading: false, error: null });
      },
      (cause) => {
        if (active) {
          setState({ data: null, loading: false, error: cause instanceof Error ? cause : new Error(String(cause)) });
        }
      },
    );
    return () => {
      active = false;
    };
  }, [...deps, nonce]);

  return { ...state, reload };
}
